import { useEffect, useRef, useCallback } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthApi from "../../api/auth/AuthenticationApi";
import { useAuth } from "../../context/AuthProvider";

const WARNING_TIME = 60 * 1000;
const PROACTIVE_REFRESH_THRESHOLD = 60 * 1000;
const IDLE_TIMEOUT = 5 * 60 * 1000;
const TICK_MS = 1000;
const SKEW_MS = 10 * 1000;

export default function SessionManager() {
  const navigate = useNavigate();
  const { logout: ctxLogout, expiresAt, isAuthenticated, isAuthReady } = useAuth();

  const warnToastId = useRef(null);
  const errorToastId = useRef(null);
  const warned = useRef(false);
  const isRefreshing = useRef(false);
  const refreshGraceUntil = useRef(0);
  const ticker = useRef(null);
  const loggedOut = useRef(false);
  const bcRef = useRef(null);
  const lastErrorAt = useRef(0);
  const expiresAtRef = useRef(expiresAt || 0);
  const authedRef = useRef(isAuthenticated);

  useEffect(() => {
    expiresAtRef.current = expiresAt || 0;
  }, [expiresAt]);

  useEffect(() => {
    authedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const remainingMs = useCallback(() => expiresAtRef.current - Date.now() - SKEW_MS, []);

  const clearWarningToast = useCallback(() => {
    if (warnToastId.current) {
      toast.dismiss(warnToastId.current);
      warnToastId.current = null;
    }
    warned.current = false;
  }, []);

  const clearErrorToast = useCallback(() => {
    if (errorToastId.current) {
      toast.dismiss(errorToastId.current);
      errorToastId.current = null;
    }
  }, []);

  const broadcast = useCallback((type, payload = {}) => {
    try {
      bcRef.current?.postMessage({ type, payload });
    } catch {}
  }, []);

  const hardLogout = useCallback(async () => {
    if (loggedOut.current) return;
    loggedOut.current = true;
    try {
      await AuthApi.logout();
    } catch {}
    if (ticker.current) {
      clearInterval(ticker.current);
      ticker.current = null;
    }
    clearWarningToast();
    clearErrorToast();
    try {
      localStorage.setItem("auth:logout", String(Date.now()));
    } catch {}
    broadcast("logout");
    ctxLogout();
    navigate("/login");
  }, [broadcast, clearWarningToast, clearErrorToast, ctxLogout, navigate]);

  const tryRefresh = useCallback(
    async (opts = {}) => {
      const { silent = true, showSuccess = false, showError = false } = opts;
      if (isRefreshing.current) return false;
      isRefreshing.current = true;
      refreshGraceUntil.current = Date.now() + 5000;
      try {
        await AuthApi.refresh();
        clearWarningToast();
        clearErrorToast();
        loggedOut.current = false;
        if (!silent && showSuccess) toast.success("Session extended");
        return true;
      } catch {
        const now = Date.now();
        if (!silent && showError) {
          if (!errorToastId.current) {
            errorToastId.current = toast.error("Couldn’t extend session. You may be logged out soon.", {
              autoClose: 4000,
            });
          } else {
            toast.update(errorToastId.current, {
              render: "Couldn’t extend session. You may be logged out soon.",
            });
          }
        } else if (now - lastErrorAt.current > 5000) {
          lastErrorAt.current = now;
        }
        await hardLogout();
        return false;
      } finally {
        isRefreshing.current = false;
      }
    },
    [clearWarningToast, clearErrorToast, hardLogout]
  );

  const showOrUpdateWarning = useCallback(
    (remaining) => {
      const sec = Math.max(0, Math.ceil(remaining / 1000));
      const content = (
        <span>
          Session will expire in <strong>{sec}s</strong>. Click to extend.
        </span>
      );
      if (!warned.current) {
        warned.current = true;
        warnToastId.current = toast.warn(content, {
          toastId: "idle-expiry-toast",
          autoClose: false,
          closeButton: true,
          position: "top-center",
          onClick: async () => {
            const ok = await tryRefresh({ silent: false, showSuccess: true, showError: true });
            if (ok) clearWarningToast();
          },
        });
      } else if (warnToastId.current) {
        toast.update(warnToastId.current, { render: content });
      }
    },
    [tryRefresh, clearWarningToast]
  );

  useEffect(() => {
    if (!isAuthReady) return undefined;
    ticker.current = setInterval(async () => {
      if (!authedRef.current) return;
      if (isRefreshing.current) return;

      const remaining = remainingMs();

      if (remaining <= 0) {
        if (Date.now() < refreshGraceUntil.current) return;
        await hardLogout();
        return;
      }

      if (remaining <= PROACTIVE_REFRESH_THRESHOLD) {
        const ok = await tryRefresh({ silent: true });
        if (!ok && remaining <= WARNING_TIME) {
          showOrUpdateWarning(remaining);
        } else if (ok && warned.current) {
          clearWarningToast();
        }
        return;
      }

      if (warned.current) clearWarningToast();
    }, TICK_MS);

    return () => {
      if (ticker.current) clearInterval(ticker.current);
    };
  }, [isAuthReady, hardLogout, tryRefresh, showOrUpdateWarning, clearWarningToast, remainingMs]);

  useEffect(() => {
    return () => {
      clearWarningToast();
      clearErrorToast();
    };
  }, [clearWarningToast, clearErrorToast]);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!authedRef.current) return;
    const remaining = remainingMs();
    if (remaining > 0 && remaining <= PROACTIVE_REFRESH_THRESHOLD) void tryRefresh({ silent: true });
  }, [isAuthReady, tryRefresh, remainingMs]);

  useEffect(() => {
    if (!isAuthReady) return;
    const onFocusLike = () => {
      if (!authedRef.current) return;
      const remaining = remainingMs();
      if (remaining > 0 && remaining <= PROACTIVE_REFRESH_THRESHOLD) void tryRefresh({ silent: true });
    };
    const onVis = () => {
      if (document.visibilityState === "visible") onFocusLike();
    };
    window.addEventListener("focus", onFocusLike);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocusLike);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isAuthReady, tryRefresh, remainingMs]);

  useEffect(() => {
    if (!isAuthReady) return;
    try {
      bcRef.current = new BroadcastChannel("auth");
      bcRef.current.onmessage = (evt) => {
        const { type } = evt.data || {};
        if (type === "logout") hardLogout();
      };
    } catch {
      bcRef.current = null;
    }
    return () => {
      try {
        bcRef.current?.close();
      } catch {}
    };
  }, [isAuthReady, hardLogout]);

  useEffect(() => {
    if (!isAuthReady) return;
    const onStorage = (e) => {
      if (e.key === "auth:logout" && e.newValue) hardLogout();
    };
    window.addEventListener("storage", onStorage);
    const onForceLogout = () => hardLogout();
    window.addEventListener("auth:force-logout", onForceLogout);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:force-logout", onForceLogout);
    };
  }, [isAuthReady, hardLogout]);

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    debounce: 500,
    crossTab: true,
    name: "app-idle-timer",
    disabled: !isAuthReady,
    onIdle: async () => {
      if (!authedRef.current) return;
      if (isRefreshing.current) return;
      const remaining = remainingMs();
      if (remaining > 0 && remaining <= PROACTIVE_REFRESH_THRESHOLD) {
        const ok = await tryRefresh({ silent: true });
        if (!ok) await hardLogout();
      } else {
        await hardLogout();
      }
    },
    onActive: async () => {
      if (!authedRef.current) return;
      const remaining = remainingMs();
      if (remaining > 0 && remaining <= PROACTIVE_REFRESH_THRESHOLD) await tryRefresh({ silent: true });
    },
    onAction: async () => {
      if (!authedRef.current) return;
      const remaining = remainingMs();
      if (remaining > 0 && remaining <= PROACTIVE_REFRESH_THRESHOLD) await tryRefresh({ silent: true });
    },
  });

  if (!isAuthReady) {
    return null;
  }

  return null;
}
